/**
 * Every dollar out this month, by where it went.
 *
 * Owner, Sep 28 2026: "Track all spending." One reading of the bank feed,
 * the Ramp card, purchases typed in by hand, the crew pay ledger and the
 * tools bought — from GET /api/money/spending. The charges nobody has sorted
 * yet are counted on their own and lead straight to the inbox below.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Plus, X } from 'lucide-react';
import { api } from '../services/api';
import { businessToday } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';
import { invalidateMoney } from '../utils/moneyQueries';

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CASH_CATEGORIES = [
  { value: 'supplies', label: 'Supplies (stock for the truck)' },
  { value: 'gas', label: 'Gas' },
  { value: 'equipment', label: 'Equipment / tools' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'admin', label: 'Admin (fees, software, insurance)' },
  { value: 'food', label: 'Food on the job' },
  { value: 'material', label: 'Material for a job' },
  { value: 'personal', label: 'Personal (not the business)' },
];

export default function SpendingMonth({ month, wholeMonth = false, onSort }: { month: string; wholeMonth?: boolean; onSort?: () => void }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [adding, setAdding] = useState(false);
  const [openCat, setOpenCat] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch, isPreviousData } = useQuery(['money-spending', month],
    () => api.get('/money/spending', { params: { month } }).then(r => r.data), { keepPreviousData: true, retry: 1 });
  const remove = useMutation((id: number) => api.delete(`/money/cash/${id}`).then(r => r.data), {
    onSuccess: () => { toast.success('Removed'); invalidateMoney(qc); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not remove that'); },
  });
  const add = useMutation((body: any) => api.post('/money/cash', body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Added'); setAdding(false); invalidateMoney(qc); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not add that'); },
  });

  if (isError) return (
    <div className="card p-4 text-sm text-amber-800 bg-amber-50 border border-amber-200">
      Could not read the spending{(error as any)?.response?.data?.message ? ` — ${(error as any).response.data.message}` : ''}.
      <button type="button" onClick={() => refetch()} className="ml-2 underline font-semibold">Try again</button>
    </div>
  );
  if (isLoading || !data) return <div className="card p-4 text-sm text-gray-500">Adding up the spending…</div>;
  // The name comes from the data, so last month's total never sits under this month's name while loading.
  const monthName = new Date(`${data.month || month}-02T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className={`card p-4 space-y-3 ${isPreviousData ? 'opacity-50' : ''}`} aria-busy={isPreviousData}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-gray-900">Spending · {wholeMonth ? `whole of ${monthName}` : monthName}{isPreviousData ? ' · updating…' : ''}</p>
          <p className="text-[11px] text-gray-500">
            {wholeMonth ? 'Spending is read a whole month at a time, not just your dates. ' : ''}
            Cards, the Ramp card, cash you typed in, and the crew. Money moved between your own accounts is left out.
          </p>
        </div>
        <p className="text-2xl font-black text-gray-900">{money(data.total)}</p>
      </div>

      {data.unsorted_count > 0 && (
        <button type="button" onClick={onSort}
          className="w-full text-left rounded-xl border bg-amber-50 border-amber-200 text-amber-900 px-4 py-2.5 text-sm">
          <span className="font-semibold">{data.unsorted_count} charge{data.unsorted_count === 1 ? '' : 's'} ({money(data.unsorted_total)}) not sorted yet</span>
          {' — '}counted as overhead by size until you file them. <span className="font-semibold">Sort them below.</span>
        </button>
      )}

      <div className="divide-y divide-gray-100">
        {data.categories.filter((c: any) => c.key !== 'unsorted').map((c: any) => (
          <div key={c.key}>
            <button type="button" onClick={() => setOpenCat(openCat === c.key ? null : c.key)}
              className="w-full flex items-baseline justify-between py-1.5 text-sm text-left">
              <span className="text-gray-700">{c.label} <span className="text-[11px] text-gray-400">· {c.count}</span></span>
              <span className="font-semibold text-gray-900">{money(c.amount)}</span>
            </button>
            {openCat === c.key && (c.items || []).length > 0 && (
              <ul className="pb-2 pl-2 space-y-0.5">
                {c.items.map((it: any, i: number) => (
                  <li key={i} className="flex justify-between gap-3 text-xs text-gray-600">
                    <span className="truncate">{it.date?.slice(5)} · {it.name}</span>
                    <span className="tabular-nums">{money(it.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        <div className="flex items-baseline justify-between py-1.5 text-sm">
          <span className="text-gray-700">Crew pay <span className="text-[11px] text-gray-400">· from the pay ledger</span></span>
          <span className="font-semibold text-gray-900">{money(Number(data.crew_pay?.paid) + Number(data.crew_pay?.owed))}
            {Number(data.crew_pay?.owed) > 0 && <span className="text-[11px] font-normal text-gray-500"> · {money(data.crew_pay.owed)} still owed</span>}</span>
        </div>
        {data.owner_pay > 0 && (
          <div className="flex items-baseline justify-between py-1.5 text-sm">
            <span className="text-gray-700">Your pay <span className="text-[11px] text-gray-400">· your own entries in the ledger</span></span>
            <span className="font-semibold text-gray-900">{money(data.owner_pay)}</span>
          </div>
        )}
        {data.tools?.total > 0 && (
          <div className="flex items-baseline justify-between py-1.5 text-sm">
            <span className="text-gray-700">Tools bought off the card <span className="text-[11px] text-gray-400">· {data.tools.items.map((t: any) => t.name).slice(0, 3).join(', ')}{data.tools.items.length > 3 ? '…' : ''}</span></span>
            <span className="font-semibold text-gray-900">{money(data.tools.total)}</span>
          </div>
        )}
      </div>

      {(data.cash_rows || []).length > 0 && (
        <div className="rounded-lg border border-gray-200 p-2.5 text-xs text-gray-700 space-y-1">
          <p className="font-semibold text-gray-900">Typed in by hand</p>
          {data.cash_rows.map((r: any) => (
            <p key={r.id} className="flex items-center justify-between gap-2">
              <span>{r.date} · {r.name}{r.on_job ? ' · on a job' : ''}</span>
              <span className="flex items-center gap-2"><b>{money(r.amount)}</b>
                {!readOnly && <button type="button" title="Take it back out" onClick={() => { if (window.confirm(`Remove "${r.name}" (${money(r.amount)})?`)) remove.mutate(r.id); }}
                  className="p-0.5 rounded text-gray-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>}</span>
            </p>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap text-[11px] text-gray-500">
        {data.pending_total > 0 && <span>{money(data.pending_total)} still pending at the bank.</span>}
        {data.personal_total > 0 && <span>{money(data.personal_total)} marked personal, not counted.</span>}
        {data.ignored_total > 0 && <span>{money(data.ignored_total)} of transfers, cash out and crew Venmo left out (the crew is counted from the ledger).</span>}
        {data.cash_typed > 0 && <span>{money(data.cash_typed)} typed in by hand.</span>}
        <button type="button" onClick={() => setAdding(true)} disabled={readOnly} title={readOnly ? 'View-only account' : undefined} className="disabled:opacity-50 ml-auto text-xs px-2.5 py-1.5 rounded-lg bg-gray-900 text-white font-semibold flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> Add a cash cost
        </button>
      </div>

      {adding && <CashModal onClose={() => setAdding(false)} onSave={b => add.mutate(b)} saving={add.isLoading} />}
    </div>
  );
}

/** A purchase that never touched a card. */
function CashModal({ onClose, onSave, saving }: { onClose: () => void; onSave: (b: any) => void; saving: boolean }) {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('supplies');
  const [date, setDate] = useState(businessToday());
  const submit = (e: React.FormEvent) => { e.preventDefault(); onSave({ amount: Number(amount), name, category, date }); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">What did you pay cash for?</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="block text-sm"><span className="text-gray-600">What</span>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Trimmer line at the hardware store" className="input w-full mt-1" required maxLength={120} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm"><span className="text-gray-600">How much</span>
            <input type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="input w-full mt-1" required /></label>
          <label className="block text-sm"><span className="text-gray-600">When</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="input w-full mt-1" required /></label>
        </div>
        <label className="block text-sm"><span className="text-gray-600">What kind</span>
          <select value={category} onChange={e => setCategory(e.target.value)} className="input w-full mt-1">
            {CASH_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
        <p className="text-[11px] text-gray-500">It joins the bank feed as a charge you typed, so overhead, the month and this list all see it. To put it on a particular job, add it on that job instead.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700">Cancel</button>
          <button type="submit" disabled={saving} className="px-3 py-2 text-sm rounded-lg bg-gray-900 text-white font-semibold disabled:opacity-50">{saving ? 'Adding…' : 'Add it'}</button>
        </div>
      </form>
    </div>
  );
}
